import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';
import { Sparkles, Tag, Package, Layers, Wrench, Megaphone } from 'lucide-react';
import { ProductCard } from '@/components/ProductCard';

const CATEGORY_META = {
  producto_nuevo: { label: 'Producto nuevo', icon: Package, tone: 'bg-emerald-100 text-emerald-900' },
  categoria_nueva: { label: 'Nueva categoría', icon: Layers, tone: 'bg-blue-100 text-blue-900' },
  promocion: { label: 'Promoción', icon: Tag, tone: 'bg-orange-100 text-orange-900' },
  plataforma: { label: 'Plataforma', icon: Wrench, tone: 'bg-slate-900 text-white' },
};

export default function Novedades() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [sponsored, setSponsored] = useState([]);

  useSEO({
    title: 'Novedades | AutoMarket Colombia',
    description:
      'Últimas novedades de AutoMarket Colombia: productos, categorías, promociones y actualizaciones de la plataforma.',
    canonical: '/novedades',
  });

  useEffect(() => {
    // Productos actualmente promocionados — se muestran destacados al inicio.
    // El endpoint filtra por `sponsored_until > now`, así que al vencer un
    // producto desaparece automáticamente del bloque.
    api.get('/products/sponsored', { params: { limit: 8 } })
      .then(({ data }) => setSponsored(data.is_fallback ? [] : (data.items || [])))
      .catch(() => setSponsored([]));
  }, []);

  useEffect(() => {
    const params = { limit: 30 };
    if (filter) params.category = filter;
    setLoading(true);
    api.get('/announcements', { params })
      .then(({ data }) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <Layout>
      <section className="border-b border-slate-200 bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center bg-orange-500 text-slate-900">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-400">Actualizaciones</div>
            <h1 className="font-display text-3xl font-black tracking-tighter" data-testid="novedades-title">
              Novedades
            </h1>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        {sponsored.length > 0 && (
          <section className="mb-10" data-testid="novedades-sponsored-section">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-8 w-8 items-center justify-center bg-orange-500 text-white">
                <Megaphone className="h-4 w-4" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-orange-600">Promocionados</div>
                <h2 className="font-display text-lg font-black text-slate-900 leading-tight">Productos destacados</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {sponsored.slice(0, 6).map((p) => (
                <ProductCard key={p.product_id} product={p} onAdd={() => {}} />
              ))}
            </div>
          </section>
        )}

        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="novedades-filters">
          <button
            data-testid="novedades-filter-todas"
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border ${filter === '' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700 hover:border-slate-500'}`}
            onClick={() => setFilter('')}
          >
            Todas
          </button>
          {Object.entries(CATEGORY_META).map(([k, m]) => (
            <button
              key={k}
              data-testid={`novedades-filter-${k}`}
              className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border ${filter === k ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700 hover:border-slate-500'}`}
              onClick={() => setFilter(k)}
            >
              {m.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-slate-500 py-12" data-testid="novedades-loading">Cargando…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-slate-500 py-12" data-testid="novedades-empty">
            Todavía no hay novedades publicadas.
          </p>
        ) : (
          <div className="space-y-6" data-testid="novedades-list">
            {items.map((n) => {
              const meta = CATEGORY_META[n.category] || CATEGORY_META.plataforma;
              const Icon = meta.icon;
              return (
                <article
                  key={n.announcement_id}
                  data-testid={`novedad-${n.announcement_id}`}
                  className="border border-slate-200 bg-white overflow-hidden md:flex"
                >
                  {n.image_url ? (
                    <img
                      src={n.image_url}
                      alt=""
                      className="h-48 w-full object-cover md:h-auto md:w-64"
                    />
                  ) : null}
                  <div className="p-6 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}>
                        <Icon className="h-3 w-3" /> {meta.label}
                      </span>
                      <span className="text-xs text-slate-500">
                        {(n.published_at || n.created_at || '').substring(0, 10)}
                      </span>
                    </div>
                    <h2 className="mt-2 font-display text-xl font-black tracking-tight text-slate-900">
                      {n.title}
                    </h2>
                    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">
                      {n.body}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!loading && items.length > 0 && (
          <p className="mt-8 text-xs text-slate-500 text-right">Total: {total}</p>
        )}
      </div>
    </Layout>
  );
}
